import { useEffect, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Text } from "styles/utility-components";

import TurnOnMFA from "components/account_management/two_factor_settings/TurnOnMFA";
import { MfaSetup } from "components/account_management/two_factor_settings/utils";
import { VerifyMFA } from "components/account_management/two_factor_settings/VerifyMFA";
import Button from "components/ui/button/Button";
import { useSession } from "lib/auth/authClient";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { routes } from "lib/routes";
import { performLogout } from "lib/userUtils";
import { Card, Page } from "./authPageStyles";
import { StotlesLogo } from "./StotlesLogo";

/**
 * Standalone 2FA enrolment page for users whose company mandates 2FA but who haven't enrolled yet.
 * The login page sends them here straight after sign-in, and the root-route guard from any other
 * page (see rootRoute.ensure2FASetup), carrying where they were heading as `redirect`; once enrolled
 * they continue on to it.
 * @returns JSX.Element
 */
export function BetterAuthTwoFactorSetupPage() {
  // Sanitised to an internal path by the route's validateSearch. `strict: false` resolves it outside
  // the route (tests).
  const { redirect } = useSearch({ strict: false });
  const destination = redirect ?? "/";
  const navigate = useNavigate();
  const { data: session, isPending } = useSession();
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  // Logout ends the better-auth session before Rails redirects, so the session drops to null
  // mid-logout; this stops that being treated as "signed out, go to login".
  const [loggingOut, setLoggingOut] = useState(false);

  const isEnabled = Boolean(session?.user?.twoFactorEnabled);
  // Enrolment runs against the better-auth session, so without one the user has to sign in again.
  const needsSignIn = !isPending && !session?.user && !loggingOut;

  // Full navigations (as the other auth pages do) so the app reloads with a fresh session and the
  // root guard's cached `twoFactorSetupRequired` flag is dropped. An SPA navigation would hit the
  // guard with that stale flag and bounce straight back here.
  useEffect(() => {
    if (isEnabled) {
      void navigate({ href: destination, reloadDocument: true });
    } else if (needsSignIn) {
      void navigate({
        href: `${routes.signIn}?redirect=${encodeURIComponent(destination)}`,
        reloadDocument: true,
      });
    }
  }, [isEnabled, needsSignIn, destination, navigate]);

  const logOut = () => {
    setLoggingOut(true);
    performLogout().catch(() => setLoggingOut(false));
  };

  if (isPending || isEnabled || loggingOut || !session?.user) return <CentredSpinner />;

  return (
    <Page>
      <Card>
        <StotlesLogo />
        <Text h1>Two-factor authentication required</Text>

        {setup ? (
          <VerifyMFA
            setup={setup}
            setSetup={setSetup}
            onSuccess={() => navigate({ href: destination, reloadDocument: true })}
          />
        ) : (
          <TurnOnMFA companyRequires2FA setSetup={setSetup} />
        )}

        {/* The only way out: these pages have no header, and every other page redirects back here. */}
        <Button buttonType="tertiary" type="button" onClick={logOut}>
          Log out
        </Button>
      </Card>
    </Page>
  );
}
